import { MonitorCog, Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

// Constants for better maintainability
const STORAGE_KEY = 'nova-user-theme';
const ANIMATION_DURATION = 300;
const ICON_SIZE_CLASSES = 'h-[1.2rem] w-[1.2rem]';

// Types for better type safety
type ThemeType = 'light' | 'dark' | 'system';

interface ThemeConfig {
  icon: React.ComponentType<{ className?: string }>;
  nextTheme: ThemeType;
  label: string;
  tooltipText: string;
}

/**
 * Theme toggle component that provides smooth transitions between light, dark, and system themes.
 * Features 360-degree rotation animation and informative tooltips.
 *
 * Behavior:
 * - First visit: Shows system preference with MonitorCog icon
 * - First click: Saves opposite of system preference to localStorage
 * - Subsequent clicks: Cycles through system → light → dark → system
 */
export const ThemeToggle: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [isRotating, setIsRotating] = useState(false);

  // Avoid hydration mismatch
  useEffect(() => {
    setMounted(true);
  }, []);

  // Memoize system preference detection to avoid repeated calculations
  const systemPreference = useMemo((): ThemeType => {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  }, []);

  // Memoize stored theme to avoid repeated localStorage access
  const storedTheme = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEY) || theme;
  }, [theme]);

  // Theme configuration mapping for better maintainability
  const themeConfigs = useMemo(
    (): Record<ThemeType, ThemeConfig> => ({
      light: {
        icon: Sun,
        nextTheme: 'dark',
        label: 'Switch to dark theme',
        tooltipText: 'Light theme active. Click for dark theme.',
      },
      dark: {
        icon: Moon,
        nextTheme: 'system',
        label: 'Switch to system theme',
        tooltipText: 'Dark theme active. Click for system preference.',
      },
      system: {
        icon: MonitorCog,
        nextTheme: 'light',
        label: 'Switch to light theme',
        tooltipText: 'Using system preference. Click for light theme.',
      },
    }),
    []
  );

  // Optimized theme change handler with useCallback to prevent unnecessary re-renders
  const handleThemeChange = useCallback(() => {
    setIsRotating(true);

    if (!storedTheme) {
      // First time - set opposite of system preference
      const oppositeTheme: ThemeType =
        systemPreference === 'dark' ? 'light' : 'dark';
      setTheme(oppositeTheme);
    } else {
      // Cycle through themes using configuration
      const currentTheme = (theme as ThemeType) || 'system';
      const nextTheme = themeConfigs[currentTheme]?.nextTheme || 'light';
      setTheme(nextTheme);
    }

    // Reset rotation animation
    const timeoutId = setTimeout(() => {
      setIsRotating(false);
    }, ANIMATION_DURATION);

    // Cleanup timeout if component unmounts
    return () => clearTimeout(timeoutId);
  }, [storedTheme, systemPreference, theme, themeConfigs, setTheme]);

  // Memoized icon component to prevent unnecessary re-renders
  const ThemeIcon = useMemo(() => {
    if (!mounted) {
      return <Sun className={ICON_SIZE_CLASSES} />;
    }

    const rotationClass = isRotating ? 'rotate-360' : '';
    const iconClasses = `${ICON_SIZE_CLASSES} ${rotationClass}`.trim();

    // Determine which icon to show
    let IconComponent: React.ComponentType<{ className?: string }>;

    if (!storedTheme) {
      // No stored theme - show system icon
      IconComponent = MonitorCog;
    } else {
      // Use current theme or fallback to system
      const currentTheme = (theme as ThemeType) || 'system';
      IconComponent = themeConfigs[currentTheme]?.icon || MonitorCog;
    }

    return <IconComponent className={iconClasses} />;
  }, [mounted, isRotating, storedTheme, theme, themeConfigs]);

  // Memoized accessibility label
  const ariaLabel = useMemo(() => {
    if (!mounted) return 'Toggle theme';

    if (!storedTheme) {
      const oppositeTheme = systemPreference === 'dark' ? 'light' : 'dark';
      return `Switch to ${oppositeTheme} theme`;
    }

    const currentTheme = (theme as ThemeType) || 'system';
    return themeConfigs[currentTheme]?.label || 'Toggle theme';
  }, [mounted, storedTheme, systemPreference, theme, themeConfigs]);

  // Memoized tooltip content
  const tooltipContent = useMemo(() => {
    if (!mounted) return 'Theme selector';

    if (!storedTheme) {
      return `Currently using system preference (${systemPreference}). Click to override.`;
    }

    const currentTheme = (theme as ThemeType) || 'system';
    return themeConfigs[currentTheme]?.tooltipText || 'Theme selector';
  }, [mounted, storedTheme, systemPreference, theme, themeConfigs]);

  // Loading state - prevent hydration mismatch
  if (!mounted) {
    return (
      <Button variant='outline' size='icon' disabled>
        <Sun className={ICON_SIZE_CLASSES} />
        <span className='sr-only'>Loading theme selector</span>
      </Button>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant='outline'
          size='icon'
          onClick={handleThemeChange}
          aria-label={ariaLabel}
          className='relative'
          type='button'
        >
          {ThemeIcon}
          <span className='sr-only'>{ariaLabel}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>{tooltipContent}</TooltipContent>
    </Tooltip>
  );
};

export default ThemeToggle;
